import { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { fetchMe } from "../api/me";

/**
 * Resolves which Firebase user id the dashboard / payouts SQL + Firestore should use.
 * Non-admins never get another user's id from ?user_id= (ignored until /me confirms admin).
 * @param {object} auth_user - from UserContext / AuthUser
 */
export function useDashboardScope(auth_user) {
  const { search } = useLocation();
  const queryUserId = useMemo(() => {
    const q = new URLSearchParams(search).get("user_id");
    return q?.trim() || "";
  }, [search]);

  const sessionUid = auth_user?.user?.uid;

  const [me, setMe] = useState({
    loaded: false,
    isAdmin: false,
    uid: null,
  });

  useEffect(() => {
    if (!auth_user?.user?.uid) {
      setMe({ loaded: true, isAdmin: false, uid: null });
      return;
    }
    let cancelled = false;
    fetchMe().then((data) => {
      if (cancelled) return;
      setMe({
        loaded: true,
        isAdmin: Boolean(data.isAdmin),
        uid: data.uid ?? null,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [auth_user?.user?.uid]);

  const effectiveUid = useMemo(() => {
    if (!me.loaded) {
      if (queryUserId) return null;
      return sessionUid || null;
    }
    if (me.isAdmin && queryUserId) return queryUserId;
    return sessionUid || null;
  }, [me.loaded, me.isAdmin, queryUserId, sessionUid]);

  const waitingForScope = Boolean(queryUserId) && !me.loaded;
  const isImpersonating = Boolean(
    me.loaded && me.isAdmin && queryUserId && queryUserId !== me.uid
  );

  return {
    sessionUid,
    effectiveUid,
    queryUserId,
    isAdmin: me.isAdmin,
    meLoaded: me.loaded,
    waitingForScope,
    isImpersonating,
    linkSearch: search || "",
  };
}
